<script setup lang="ts">
import ListingDetailBasic from '@/modules/realestate/views/ListingDetailBasic.vue'
import ListingDetailContent from '@/modules/realestate/views/ListingDetailContent.vue'
import ListingDetailMedia from '@/modules/realestate/views/ListingDetailMedia.vue'
import ListingDetailAsset from '@/modules/realestate/views/ListingDetailAsset.vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  useListingListStore,
  ListingListActionTypes,
} from '@/modules/realestate/stores/listing.list.store.ts'
import '@/modules/realestate/assets/scss/listing.scss'
import { Icon } from '@iconify/vue'
import ListingDetailContract from '@/modules/realestate/views/ListingDetailContract.vue'
import ContractUpdateForm from '@/modules/contract/components/ContractUpdateForm.vue'
import ContractDetailForm from '@/modules/contract/components/ContractDetailForm.vue'
import { useToast } from 'primevue/usetoast'
import {
  initialValues as _initialValues,
} from '@/modules/realestate/helper/listing'
import { LISTING_TYPE } from '@/modules/realestate/constants/realestate.const'
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import { schemaListing } from '../helper/schema'
import { yupResolver } from '@primevue/forms/resolvers/yup'

import { useConfirm } from 'primevue'
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue';

const toast = useToast()
const confirm = useConfirm()
const router = useRouter()

interface Props {
  id?: number
  approveForm?: boolean
}

const listingListStore = useListingListStore()

const props = defineProps<Props>()
const initialValues = ref(_initialValues)

const editable = ref<boolean>(false)
const contractCreateFormDisplay = ref<boolean>(false)
const contractDetailFormDisplay = ref<boolean>(false)
const contractId = ref<number | null>(null)

const formRef = ref()

const resolver = yupResolver(schemaListing)

const onFormSubmit = async ({
  values,
  valid,
  errors,
}: {
  values: any
  valid: boolean
  errors: any
}) => {
  if (!valid) {
    return
  }
  // get form values
  confirm.require({
    message: 'Bạn có muốn tạo mới tin đăng này?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: async () => {
      console.log('onFormSubmit called', values)
      try {
        await listingListStore.createListing(values)

        initialValues.value = values
        formRef.value?.reset()
        toast.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'You have created',
          life: 3000,
        })
        listingListStore.fetchList()
        router.push({ name: 'property-listings' })
      } catch (e) {
        console.error('Error formatting data:', e)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: e?.message || 'Đã có lỗi xảy ra',
          life: 3000,
        })
        return
      }
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const handleEditing = (e: Event) => {
  editable.value = true
}

const loading = computed(() => {
  return listingListStore?.action_type === ListingListActionTypes.CREATING
})

const handleSave = (e: Event) => {
  editable.value = false
  const formEl = formRef.value.$el as HTMLFormElement

  const event = new Event('submit', {
    bubbles: true, // Whether the event will bubble up through the DOM or not
    cancelable: true, // Whether the event may be canceled or not
  })

  formEl.dispatchEvent(event)
}
const handleReset = (e: Event) => {
  editable.value = false
  // reset form
  formRef.value?.reset()
  contractId.value = initialValues.value.contract_id
}

const handleCreatedContract = (data: any) => {
  if (data.id) {
    contractCreateFormDisplay.value = false
    contractId.value = data.id
    formRef.value?.setFieldValue('customer_id', data.seller?.id)
    formRef.value?.setFieldValue('broker_id', data.seller_broker?.id)
    formRef.value?.setFieldValue('contract_id', data.id)
  }
}

const handleChangeDetailContract = (data: any) => {
  if (data?.id) {
    contractId.value = data.id
    formRef.value?.setFieldValue('customer_id', data.seller?.id)
    formRef.value?.setFieldValue('broker_id', data.seller_broker?.id)
    formRef.value?.setFieldValue('contract_id', data.id)
  } else {
    contractId.value = null
    formRef.value?.setFieldValue('customer_id', null)
    formRef.value?.setFieldValue('broker_id', null)
    formRef.value?.setFieldValue('contract_id', null)
  }
}
</script>

<template>
  <DashboardLayout currentRoute="property/listings/create" page-title="Tin đăng BDS • Tạo mới">
    <div class="">
      <div class="flex justify-end gap-4 mb-5">
        <div class="flex gap-2">
          <button class="btn btn-success flex items-center gap-3" @click="handleSave">
            <Icon icon="ri:save-3-fill" width="16px" height="16px" /> Lưu
            <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loading" />
          </button>
          <button class="btn btn-outline flex items-center gap-3" @click="handleReset">
            <Icon icon="ri:reset-left-fill" width="16px" height="16px" /> Reset
          </button>
        </div>
      </div>
      <Form
        ref="formRef"
        v-slot="$form"
        :initialValues
        :resolver
        @submit="onFormSubmit"
        class="flex flex-col md:flex-row w-full gap-3 mb-5"
      >
        <div class="flex-3 flex flex-col gap-3">
          <Panel header="Thông tin cơ bản" toggleable>
            <ListingDetailBasic editable :form="formRef" />
          </Panel>
          <Panel header="Nội dung" toggleable>
            <ListingDetailContent editable :form="formRef" />
          </Panel>
          <Panel header="Media" toggleable>
            <ListingDetailMedia editable :form="formRef" />
          </Panel>
        </div>
        <div class="flex-2 flex flex-col gap-3">
          <Panel header="Thông tin BĐS" toggleable>
            <ListingDetailAsset editable :form="formRef" />
          </Panel>
          <Panel
            toggleable
            v-if="
              [LISTING_TYPE.EXCLUSIVE, LISTING_TYPE.OWNER].includes(
                $form?.listing_type?.value?.code,
              )
            "
          >
            <template #header>
              <div class="flex w-full items-center justify-between">
                <div class="p-panel-title">Thông tin hợp đồng</div>
                <AllowPerm perm="contract.create" action="hide">
                  <div class="text-right">
                    <a
                      href="#"
                      class="flex items-center hover:text-[#0000FF]"
                      @click="
                        () => {
                          contractCreateFormDisplay = true
                        }
                      "
                    >
                      <Icon icon="ic:baseline-plus" /> Thêm hợp đồng
                    </a>
                  </div>
                </AllowPerm>
              </div>
            </template>
            <ListingDetailContract
              :contract-id="contractId"
              :listingId="props.id"
              editable
              @change="handleChangeDetailContract"
              @show:detail="
                () => {
                  contractDetailFormDisplay = true
                }
              "
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ $form?.contract_id?.error?.message }}
            </span>
          </Panel>
        </div>
      </Form>
    </div>
    <Drawer v-model:visible="contractCreateFormDisplay" header="Tạo hợp đồng mới" position="right">
      <div class="drawer-80vw">
        <ContractUpdateForm
          :listing-id="props.id"
          :contract-id="null"
          action="create"
          @on:created="handleCreatedContract"
        />
      </div>
    </Drawer>
    <Drawer v-model:visible="contractDetailFormDisplay" header="Chi tiết hợp đồng" position="right">
      <div class="drawer-80vw">
        <contract-detail-form :contract-id="contractId" />
      </div>
    </Drawer>
  </DashboardLayout>
</template>

<style scoped></style>
